<script setup lang="ts">
import { onMounted, onUnmounted, ref, useId } from 'vue';

const SIZES = {
  sm: 'max-w-[532px]',
  base: 'max-w-[560px]',
  md: 'max-w-[640px]',
  lg: 'max-w-[860px]',
  xl: 'max-w-[900px]',
  xxl: 'max-w-[980px]',
} as const;

const props = withDefaults(
  defineProps<{ size?: keyof typeof SIZES; dismissible?: boolean }>(),
  { size: 'md', dismissible: true },
);
const emit = defineEmits<{ close: [] }>();

const titleId = useId();
const panel = ref<HTMLElement>();
const previouslyFocused = document.activeElement as HTMLElement | null;

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.dismissible) emit('close');
};

onMounted(() => panel.value?.focus());
onUnmounted(() => previouslyFocused?.focus?.());
</script>

<template>
  <Teleport to="body">
    <div
      class="font-pds bg-voile fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5"
      @mousedown.self="dismissible && emit('close')"
      @keydown="onKeydown"
    >
      <div
        ref="panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
        class="bg-carte text-encre flex max-h-full w-full flex-col overflow-hidden rounded-[32px] outline-none"
        :class="SIZES[size]"
      >
        <header class="border-filet-doux border-b px-5 pt-[25px] pb-[18px]">
          <h2
            :id="titleId"
            class="text-[24px] leading-[1.15] font-bold tracking-[-0.2px]"
          >
            <slot name="title" />
          </h2>
          <p
            v-if="$slots.subtitle"
            class="text-encre-douce mt-[6px] flex items-center gap-1 text-[14px] leading-[1.4]"
          >
            <slot name="subtitle" />
          </p>
        </header>
        <div class="no-scrollbar min-h-0 flex-1 overflow-y-auto">
          <slot />
        </div>
        <footer
          v-if="$slots.footer"
          class="border-filet-doux flex flex-wrap items-center gap-3 border-t p-4 sm:gap-4 sm:p-5"
        >
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>
